import { CheckCircle, HelpCircle, XCircle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SystemHealthData } from '../../types';
import { cn } from '../../lib/utils';

interface SystemHealthCardProps {
  health: SystemHealthData;
  className?: string;
}

// Health status badge component
const HealthBadge: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const normalizedStatus = status?.toLowerCase() || 'unknown';
  
  const getStatusConfig = () => {
    switch (normalizedStatus) {
      case 'healthy':
        return {
          icon: CheckCircle,
          bgColor: 'bg-green-100 dark:bg-green-950/30',
          textColor: 'text-green-700 dark:text-green-400',
          iconColor: 'text-green-500',
        };
      case 'unhealthy':
        return {
          icon: XCircle,
          bgColor: 'bg-red-100 dark:bg-red-950/30',
          textColor: 'text-red-700 dark:text-red-400',
          iconColor: 'text-red-500',
        };
      default:
        return {
          icon: HelpCircle,
          bgColor: 'bg-gray-100 dark:bg-gray-800',
          textColor: 'text-gray-700 dark:text-gray-400',
          iconColor: 'text-gray-500',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className={cn(
      'flex items-center gap-3 p-3 rounded-lg',
      config.bgColor
    )}>
      <Icon className={cn('h-6 w-6', config.iconColor)} />
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className={cn('text-xs capitalize', config.textColor)}>
          {normalizedStatus}
        </p>
      </div>
    </div>
  );
};

export const SystemHealthCard: React.FC<SystemHealthCardProps> = ({ health, className }) => {
  const { t } = useTranslation();

  // List of components to display
  const components = [
    { key: 'backoffice', status: health.backoffice, label: t('forgeDashboard.health.backoffice', 'Backoffice') },
    { key: 'traefik', status: health.traefik, label: t('forgeDashboard.health.traefik', 'Traefik') },
    {
      key: 'database',
      status: health.postgres || health.mongodb,
      label: t('forgeDashboard.health.postgres', 'PostgreSQL'),
    },
    { key: 'argocd', status: health.argocd, label: t('forgeDashboard.health.argocd', 'ArgoCD') },
    { key: 'tekton', status: health.tekton, label: t('forgeDashboard.health.tekton', 'Tekton') },
  ];

  // Calculate overall health
  const healthyCount = components.filter(c => c.status?.toLowerCase() === 'healthy').length;
  const totalCount = components.length;
  const overallHealthy = healthyCount === totalCount;

  return (
    <div className={cn('bg-card border border-border rounded-xl p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {t('forgeDashboard.health.title', 'System Health')}
        </h3>
        <div className={cn(
          'px-3 py-1 rounded-full text-xs font-medium',
          overallHealthy 
            ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400'
        )}>
          {healthyCount}/{totalCount} {t('forgeDashboard.health.healthy', 'Healthy')}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {components.map((component) => (
          <HealthBadge
            key={component.key}
            status={component.status}
            label={component.label}
          />
        ))}
      </div>

      {/* Environment and timestamp info */}
      {(health.environment || health.timestamp) && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          {health.environment && (
            <span className="capitalize">
              {t('forgeDashboard.health.environment', 'Environment')}: {health.environment}
            </span>
          )}
          {health.timestamp && (
            <span>
              {t('forgeDashboard.health.lastCheck', 'Last check')}: {new Date(health.timestamp).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemHealthCard;
